import { FaSearch, FaExclamationTriangle, FaInfoCircle, FaSyncAlt } from "react-icons/fa";

// Composants
import AccountRow from "./AccountRow";

const AccountRowSkeleton = () => (
  <tr className="skeleton-row animate__animated animate__fadeIn">
    <td>
      <div className="d-flex align-items-center gap-3">
        <div className="skeleton skeleton-avatar position-relative overflow-hidden" 
             style={{ 
               width: '50px', 
               height: '50px', 
               borderRadius: '50%',
               background: 'linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%)',
               backgroundSize: '200% 100%',
               animation: 'skeleton-shimmer 1.8s infinite'
             }}>
          <div style={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width: '28px',
            height: '28px',
            borderRadius: '50%',
            background: 'rgba(255,255,255,0.4)',
            animation: 'skeleton-pulse 1.4s infinite'
          }} />
        </div>
        
        <div style={{ 
          width: '120px', 
          height: '18px', 
          background: 'linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%)',
          backgroundSize: '200% 100%',
          borderRadius: '8px',
          animation: 'skeleton-shimmer 1.8s infinite'
        }} />
        <div style={{ 
          width: '80px', 
          height: '14px', 
          background: 'linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%)',
          backgroundSize: '200% 100%',
          borderRadius: '6px',
          animation: 'skeleton-shimmer 1.8s 0.2s infinite',
          opacity: 0.8
        }} />
      </div>
    </td>
    
    <td>
      <div style={{ 
        width: '160px', 
        height: '16px', 
        background: 'linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%)',
        backgroundSize: '200% 100%',
        borderRadius: '6px',
        animation: 'skeleton-shimmer 1.8s infinite'
      }} />
    </td>
    
    <td>
      <div style={{ 
        width: '56px', 
        height: '22px', 
        background: 'linear-gradient(90deg, #e3f2fd 25%, #bbdefb 50%, #e3f2fd 75%)',
        backgroundSize: '200% 100%',
        borderRadius: '12px',
        animation: 'skeleton-shimmer 1.8s infinite'
      }} />
    </td>
    
    <td>
      <div style={{ 
        width: '110px', 
        height: '16px', 
        background: 'linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%)',
        backgroundSize: '200% 100%',
        borderRadius: '6px',
        animation: 'skeleton-shimmer 1.8s 0.1s infinite'
      }} />
    </td>
    
    <td className="text-end">
      <div style={{ 
        width: '72px', 
        height: '18px', 
        background: 'linear-gradient(90deg, #f5f5f5 25%, #e8e8e8 50%, #f5f5f5 75%)',
        backgroundSize: '200% 100%',
        borderRadius: '6px',
        marginLeft: 'auto',
        animation: 'skeleton-shimmer 1.8s 0.3s infinite'
      }} />
    </td>
    
    <td className="text-end">
      <div style={{ 
        width: '68px', 
        height: '18px', 
        background: 'linear-gradient(90deg, #f5f5f5 25%, #e8e8e8 50%, #f5f5f5 75%)',
        backgroundSize: '200% 100%',
        borderRadius: '6px',
        marginLeft: 'auto',
        animation: 'skeleton-shimmer 1.8s 0.4s infinite'
      }} />
    </td>
    
    <td className="text-center">
      <div style={{ 
        width: '85px', 
        height: '26px', 
        background: 'linear-gradient(90deg, #f3e5f5 25%, #e1bee7 50%, #f3e5f5 75%)',
        backgroundSize: '200% 100%',
        borderRadius: '14px',
        margin: '0 auto',
        animation: 'skeleton-shimmer 1.8s 0.2s infinite'
      }} />
    </td>
    
    <td className="text-center">
      <div style={{ 
        width: '95px', 
        height: '26px', 
        background: 'linear-gradient(90deg, #e8f5e8 25%, #c8e6c9 50%, #e8f5e8 75%)',
        backgroundSize: '200% 100%',
        borderRadius: '14px',
        margin: '0 auto',
        animation: 'skeleton-shimmer 1.8s 0.5s infinite'
      }} />
    </td>
    
    <td className="text-center">
      <div className="d-flex gap-2 justify-content-center">
        {[...Array(5)].map((_, i) => (
          <div key={i} className="skeleton skeleton-icon" 
               style={{ 
                 width: '34px', 
                 height: '34px', 
                 borderRadius: '6px',
                 background: 'linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%)',
                 backgroundSize: '200% 100%',
                 animation: `skeleton-shimmer 1.8s ${i * 0.1}s infinite`
               }} />
        ))}
      </div>
    </td>
  </tr>
);

/**
 * TABLEAU DES COMPTES - Version OPTIMISÉE ET PROFESSIONNELLE
 */
const AccountsTable = ({ 
  loading = false,
  error = null,
  noResults = false,
  accounts = [],
  selectedDomain,
  handleOpenSignatoriesModal, 
  handleOpenDetailsModal, 
  handleOpenValidationModal, 
  isCurrentUserValidator, 
  handleOpenModalOrders, 
  handleOpenCashEditModal, 
  handleOpenModalSecurities, 
  handleOpenModalTransactions, 
  handleShowEditAccountModal, 
  handleDeleteSuccess, 
  handleGenerateTitleReport, 
  handleGenerateTransactionReport, 
  openContactForm, 
  openContactList, 
  openModal, 
  handleOpenModal, 
  openAssociationForm, 
  openAssociationList, 
  handleOpenApprovalDetailsModal, 
  handleOpenSubmitAccountConfirmation,
  handleOpenValidationDetails,
  onRetry,
  searchTerm = '',
  accountTypeFilter = '',
  statusFilter = '',
  handleOpenManagedAccounts,
  handleOpenHistoricsDocuments
}) => {
  const isFiltering = !!searchTerm || !!accountTypeFilter || !!statusFilter;
  const showSkeletons = loading && accounts.length === 0;
  
  const renderTableContent = () => {
    if (showSkeletons) {
      return [...Array(8)].map((_, i) => (
        <AccountRowSkeleton key={`skeleton-${i}`} />
      ));
    }

    if (error) {
      return (
        <tr>
          <td colSpan="9" className="text-center py-5">
            <div className="error-state bg-light rounded p-4 border border-danger">
              <FaExclamationTriangle className="text-danger mb-3" size={48} />
              <h5 className="text-danger mb-3">Erreur de chargement</h5>
              <p className="text-muted mb-4">{error}</p>
              <button className="btn btn-primary" onClick={onRetry}>
                <FaSyncAlt className="me-2" />
                Réessayer
              </button>
              {(error.includes("session") || error.includes("authentification")) && (
                <div className="mt-3">
                  <small className="text-muted">
                    <FaInfoCircle className="me-1" />
                    Essayez de vous reconnecter à l'application.
                  </small>
                </div>
              )}
            </div>
          </td>
        </tr>
      );
    }

    if (noResults && accounts.length === 0) {
      const getNoResultsMessage = () => {
        if (searchTerm) {
          return {
            title: "Aucun compte trouvé",
            message: `Aucun compte ne correspond à "${searchTerm}".`,
            suggestion: "Vérifiez l'orthographe ou essayez d'autres termes."
          };
        }
        if (accountTypeFilter) {
          return {
            title: "Aucun compte de ce type",
            message: `Aucun compte de type "${accountTypeFilter}".`,
            suggestion: "Essayez un autre type ou supprimez le filtre."
          };
        }
        if (statusFilter) {
          const statusLabels = {
            "0": "Brouillon", "1": "En attente", "2": "Approuvé", "3": "Rejeté"
          };
          return {
            title: "Aucun compte avec ce statut",
            message: `Aucun compte avec le statut "${statusLabels[statusFilter]}".`,
            suggestion: "Essayez un autre statut ou supprimez le filtre."
          };
        }
        return {
          title: "Aucun compte disponible",
          message: "Aucun compte n'a été créé dans ce domaine.",
          suggestion: "Commencez par créer un nouveau compte."
        };
      };

      const { title, message, suggestion } = getNoResultsMessage();

      return (
        <tr>
          <td colSpan="9" className="text-center py-5">
            <div className="no-results-state bg-light rounded p-4 border">
              <FaSearch className="text-muted mb-3 opacity-50" size={48} />
              <h5 className="text-muted mb-2">{title}</h5>
              <p className="text-muted mb-3">{message}</p>
              <div className="suggestions mt-3">
                <small className="text-muted">
                  <FaInfoCircle className="me-1" />
                  {suggestion}
                </small>
              </div>
            </div>
          </td>
        </tr>
      );
    }

    if (accounts.length > 0) {
      return accounts.map((account) => (
        <AccountRow
          key={account.id}
          account={account}
          selectedDomain={selectedDomain}
          handleOpenSignatoriesModal={handleOpenSignatoriesModal}
          handleOpenDetailsModal={handleOpenDetailsModal}
          handleOpenValidationModal={handleOpenValidationModal}
          isCurrentUserValidator={isCurrentUserValidator}
          handleOpenModalOrders={handleOpenModalOrders}
          handleOpenCashEditModal={handleOpenCashEditModal}
          handleOpenModalSecurities={handleOpenModalSecurities}
          handleOpenModalTransactions={handleOpenModalTransactions}
          handleShowEditAccountModal={handleShowEditAccountModal}
          handleDeleteSuccess={handleDeleteSuccess}
          handleGenerateTitleReport={handleGenerateTitleReport}
          handleGenerateTransactionReport={handleGenerateTransactionReport}
          openContactForm={openContactForm}
          openContactList={openContactList}
          openModal={openModal}
          handleOpenModal={handleOpenModal}
          openAssociationForm={openAssociationForm}
          openAssociationList={openAssociationList}
          handleOpenApprovalDetailsModal={handleOpenApprovalDetailsModal}
          handleOpenSubmitAccountConfirmation={handleOpenSubmitAccountConfirmation}
          handleOpenValidationDetails={handleOpenValidationDetails}
          handleOpenManagedAccounts={handleOpenManagedAccounts}
          handleOpenHistoricsDocuments={handleOpenHistoricsDocuments} 
        />
      ));
    }

    if (isFiltering && !loading) {
      return [...Array(4)].map((_, i) => (
        <AccountRowSkeleton key={`filter-skeleton-${i}`} />
      ));
    }

    return null;
  };

  return (
    <div className="accounts-table-container">
      <div className="table-responsive-custom">
        <table className="accounts-table">
          {(accounts.length > 0 || isFiltering) && (
            <thead>
              <tr>
                <th>Nom</th>
                <th>Email</th>
                <th>Administré/Co-titulaire</th>
                <th>Téléphone</th>
                <th className="text-end">AUC</th>
                <th className="text-end">Cash</th>
                <th className="text-center">Type</th>
                <th className="text-center">Validation</th>
                <th className="text-center">Actions</th>
              </tr>
            </thead>
          )}
          
          <tbody>{renderTableContent()}</tbody>
        </table>
      </div>
    </div>
  );
};

export default AccountsTable;
